/**
 * Talking vs. scenes (§14). The sim decides which mode you're in from state,
 * not the model "feeling it out". Leverage present, open ask, or he's angry
 * at you → scene. Otherwise → talking.
 */

import { openObligationsBetween } from './memory/facts.js';
import type { NpcId, WorldState } from '../sim/types/index.js';

export type ConversationMode = 'talking' | 'scene';

export interface ModeReasons {
  readonly angry: boolean;
  readonly leverage: boolean;
  readonly openAsk: boolean;
  readonly grievance: boolean;
}

export function modeReasons(state: WorldState, npcId: NpcId): ModeReasons {
  const npc = state.npcs[npcId];
  if (!npc) return { angry: false, leverage: false, openAsk: false, grievance: false };

  const angry =
    (npc.truth.mood.mood === 'angry' && npc.truth.mood.intensity >= 0.5) ||
    npc.truth.relationship.trust <= -20;

  // You know something he'd rather you didn't.
  const leverage = npc.truth.secrets.some((id) => {
    const secret = state.secrets[id];
    return secret !== undefined && secret.knownBy.includes('player');
  });

  // Yours, or your dependent's: the cold open's creditor is a scene, not a chat (§6).
  const openAsk =
    openObligationsBetween(state, 'player', npcId).length > 0 ||
    openObligationsBetween(state, state.player.dependent, npcId).some((f) => f.kind === 'obligation' && f.to === npcId) ||
    Object.values(state.appointments).some(
      (a) =>
        a.status.kind === 'pending' &&
        a.participants.includes('player') &&
        a.participants.includes(npcId),
    );

  const grievance = Object.values(state.grievances).some(
    (g) => g.polarity === 'grievance' && g.holder === npcId && g.against === 'player' && g.status !== 'settled',
  );

  return { angry, leverage, openAsk, grievance };
}

export function conversationMode(state: WorldState, npcId: NpcId): ConversationMode {
  const r = modeReasons(state, npcId);
  return r.angry || r.leverage || r.openAsk || r.grievance ? 'scene' : 'talking';
}
